import { useState } from "react";
import API from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Sparkle, ArrowClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const KINDS = {
  scope: {
    title: "AI Scope of Works",
    intro: "Tell the AI what the job involves in plain English. It uses the quote title, property and line items too, and writes a professional scope you can edit before inserting.",
    briefLabel: "Brief",
    placeholder: "e.g. Replace failed combi boiler like-for-like, new flue and magnetic filter, gas safety cert, remove old unit",
    minBrief: 8,
    url: "/quotes/ai/scope-of-works",
    field: "scope_of_works",
    noun: "scope",
    testid: "ai-scope",
  },
  caveats: {
    title: "AI Caveats",
    intro: "Refine the caveats you already have, or draft new exclusions and assumptions from the scope of works. Add an instruction if you want something specific (e.g. \"add a parking caveat and make them shorter\").",
    briefLabel: "Instruction (optional when caveats or a scope already exist)",
    placeholder: "e.g. Tighten the wording, add a caveat about out-of-hours access and remove the asbestos one",
    minBrief: 0,
    url: "/quotes/ai/caveats",
    field: "caveats",
    noun: "caveats",
    testid: "ai-caveats",
  },
};

/* AI drafting for a quote narrative box (Scope of Works or Caveats): brief → editable draft → Insert / Replace / Append.
   `entity` only changes the wording ("quote" | "job" | "request"); `noCaveats` hides the suggested-caveats add-on. */
export default function AiScopeDialog({ open, onOpenChange, kind = "scope", quote, quoteId, existingScope = "", existingCaveats = "", onApply, entity = "quote", noCaveats = false }) {
  const k = KINDS[kind] || KINDS.scope;
  const intro = entity === "quote" ? k.intro : k.intro.replace("the quote title, property and line items", `the ${entity} title, client and property`);
  const existing = kind === "caveats" ? existingCaveats : existingScope;
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [text, setText] = useState("");
  const [addCaveats, setAddCaveats] = useState(true);
  const t = (s) => `${k.testid}-${s}`;

  const generate = async () => {
    if (brief.trim().length < k.minBrief) { toast.error("Describe the works in a sentence or two first"); return; }
    if (kind === "caveats" && !brief.trim() && !existing.trim() && !existingScope.trim()) { toast.error("Add an instruction, a scope of works or some caveats to refine first"); return; }
    setBusy(true);
    try {
      const body = { brief, quote, quote_id: quoteId || undefined, existing };
      if (kind === "caveats") body.scope = existingScope;
      const { data } = await API.post(k.url, body);
      setDraft(data); setText(data[k.field] || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "The AI couldn't draft that just now");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setDraft(null); setText(""); setBrief(""); };
  const apply = (mode) => {
    onApply({ kind, text: text.trim(), mode, caveats: kind === "scope" && addCaveats && !noCaveats && draft?.caveats ? draft.caveats : "" });
    onOpenChange(false);
    reset();
    toast.success(mode === "append" ? `${k.title.replace("AI ", "")} appended` : `${k.title.replace("AI ", "")} ${existing.trim() ? "replaced" : "inserted"}`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={t("dialog")}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-[Chivo]"><Sparkle size={18} weight="fill" className="text-indigo-500" /> {k.title}</DialogTitle>
          <DialogDescription>{intro}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {kind === "caveats" && existing.trim() && (
            <div className="rounded-md border border-border bg-muted/30 p-3">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Current caveats (will be refined)</p>
              <pre className="whitespace-pre-wrap font-sans text-xs" data-testid={t("existing")}>{existing}</pre>
            </div>
          )}
          <div>
            <Label className="text-xs">{k.briefLabel}</Label>
            <Textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={3} autoFocus className="mt-1 text-sm" placeholder={k.placeholder} data-testid={t("brief")} />
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={generate} disabled={busy} data-testid={t("generate")}>
              {busy ? <ArrowClockwise size={14} className="mr-1 animate-spin" /> : <Sparkle size={14} className="mr-1" weight="fill" />}
              {busy ? "Drafting…" : draft ? "Regenerate" : kind === "caveats" && existing.trim() ? "Refine caveats" : `Generate ${k.noun}`}
            </Button>
            {busy && <span className="text-xs text-muted-foreground">Usually takes 10–20 seconds</span>}
          </div>
          {draft && (
            <>
              <div>
                <Label className="text-xs">Draft — edit freely</Label>
                <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={12} className="mt-1 text-xs font-mono" data-testid={t("result")} />
              </div>
              {kind === "scope" && draft.caveats && !noCaveats && (
                <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/20 p-3">
                  <label className="flex items-start gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={addCaveats} onChange={(e) => setAddCaveats(e.target.checked)} className="mt-0.5" data-testid="ai-scope-add-caveats" />
                    <span><span className="font-semibold">Also add suggested caveats</span> (appended to the Caveats box)
                      <pre className="mt-1.5 whitespace-pre-wrap font-sans text-[11px] text-amber-900 dark:text-amber-200" data-testid="ai-scope-caveats">{draft.caveats}</pre></span>
                  </label>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid={t("cancel")}>Cancel</Button>
                {existing.trim() && <Button variant="outline" size="sm" onClick={() => apply("append")} data-testid={t("append")}>Append to existing</Button>}
                <Button size="sm" onClick={() => apply("replace")} disabled={!text.trim()} data-testid={t("use")}>{existing.trim() ? `Replace ${k.noun}` : `Insert ${k.noun}`}</Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
